/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description getHoodPropertyText
 */

import { PropertyType } from '@zcode/enums'
import { memoizeState } from '@zcode/memoizers'
import { capitalize, getVerticalsArray } from '@zcode/nr-utils'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import { capitalize as toTitleCase } from '@zcode/utils'
import type { Location } from 'history'
import { getPropertyTypeTitle } from './getPropertyTypeTitle'

const EXCLUDED_PROPERTY_TYPES = [
  PropertyType.APARTMENT_CONDO_TOWNHOUSE,
  PropertyType.FLOOR_PLAN,
  PropertyType.INCOME_INVESTMENT,
  PropertyType.MOBILE_MANUFACTURED,
  PropertyType.TIC,
  PropertyType.UNKNOWN,
]

export const createGetHoodPropertyText = memoizeState(
  (location: Location<unknown>) => (entity: Listable | Pin<RawPinInventorySearch>) => {
    const { city, neighborhoodName, propertyType } = entity

    const propertyTypeTitle = getPropertyTypeTitle(propertyType)
    const verticalsArray = getVerticalsArray(location)
    const verticalText = verticalsArray[0] ? toTitleCase(verticalsArray[0]) : ''
    const propertyText =
      propertyTypeTitle && propertyType && !EXCLUDED_PROPERTY_TYPES.includes(propertyType)
        ? (`${propertyTypeTitle.toLowerCase()} for rent` as const)
        : 'apartment for rent'

    if (verticalText && neighborhoodName && city) {
      return `${verticalText} ${neighborhoodName} ${propertyText} in ${city}`
    } else if (verticalText && city) {
      return `${verticalText} ${propertyText} in ${city}`
    } else if (neighborhoodName && city) {
      if (neighborhoodName === city) {
        return `${capitalize(propertyText)} in ${city}`
      }
      return `${neighborhoodName} ${propertyText} in ${city}`
    } else if (city) {
      return `${capitalize(propertyText)} in ${city}`
    }

    return capitalize(propertyText)
  }
)
